import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigurationService } from './configuration.service';
import { filter, Observable, Subject, switchMap } from 'rxjs';
import { Collection, CollectionsLinks, CommonDimensions, FileMetadata } from '../models/collection.interface';
import { forkJoinWithProgress, UploadProgressSubject } from '../shared/fork.join.progress';
import { DriftConfiguration } from '../models/configuration.interface';
@Injectable({
  providedIn: 'root'
})
export class CollectionsService {
  config: DriftConfiguration;
  constructor(
    private http: HttpClient,
    private configurationService: ConfigurationService
  ) {
    this.config = {};

    this.configurationService
      .configurationLoaded()
      .pipe(filter(loaded => loaded === true))
      .subscribe(() => {
        this.config = this.configurationService.getConfiguration();
      });
  }
  getCollectionData(collectionId: string): Observable<FileMetadata[]> {
    return this.http.get<FileMetadata[]>(`${this.config.driftApi}/collections/${collectionId}`);
  }
  updateCollection(collectionId: string, collectionData: Collection): Observable<Collection> {
    const requestBody = {
      collection_name: collectionData.name,
      description: collectionData.description,
      access_list: collectionData.access_list,
      dimensions: collectionData.dimensions,
      is_private: collectionData.is_private,
      is_hidden: collectionData.is_hidden
    };
    return this.http.put<Collection>(`${this.config.driftApi}/collections/${collectionId}`, requestBody);
  }
  createNewCollection(collectionData: Collection): Observable<Collection> {
    const requestBody = {
      collection_name: collectionData.name,
      description: collectionData.description,
      access_list: collectionData.access_list,
      dimensions: collectionData.dimensions,
      is_private: collectionData.is_private,
      context: collectionData.context
    };
    return this.http.post<Collection>(`${this.config.driftApi}/collections`, requestBody);
  }
  uploadFilesToCollection(
    collectionId: string,
    files: FileList,
    progressSubject: Subject<UploadProgressSubject>
  ): Observable<object> {
    const fileArray = Array.from(files);
    const data = { files: fileArray.map(file => file.name) };

    return this.http.post<CollectionsLinks>(`${this.config.driftApi}/collections/${collectionId}/links`, data).pipe(
      switchMap(response => {
        const httpSessions: Observable<object>[] = [];
        response.links.forEach(link => {
          const file = fileArray.find(f => f.name === link.file_name);
          if (file) {
            httpSessions.push(this.http.put(link.url, file, { reportProgress: true, observe: 'events' }));
          } else {
            console.error(`Upload details missing for file: ${link.file_name}`);
          }
        });
        return forkJoinWithProgress(httpSessions, progressSubject);
      })
    );
  }
  updateFileMetadata(collectionId: string, fileId: string, metadata: CommonDimensions): Observable<FileMetadata> {
    return this.http.put<FileMetadata>(`${this.config.driftApi}/collections/${collectionId}/files/${fileId}`, metadata);
  }
  // TODO: Implement delete functionality if needed
  deleteFilesFromCollection(collectionId: string, fileIds: string[]): Observable<void> {
    return this.http.delete<void>(`${this.config.driftApi}/collections/${collectionId}/files`, {
      body: { file_ids: fileIds }
    });
  }

  getCollections(includeHidden = false): Observable<Collection[]> {
    const url = includeHidden
      ? `${this.config.driftApi}/collections?include_hidden=true`
      : `${this.config.driftApi}/collections`;
    return this.http.get<Collection[]>(url);
  }
  downloadFileFromCollection(
    collectionId: string,
    original_document_uri: string
  ): Observable<{ file_name: string; url: string }> {
    return this.http.post<{ file_name: string; url: string }>(
      `${this.config.driftApi}/collections/${collectionId}/download`,
      {
        uri: original_document_uri
      }
    );
  }

  copyDocumentsToCollection(
    sourceCollectionId: string,
    targetCollectionId: string,
    documentIds: string[]
  ): Observable<void> {
    return this.http.post<void>(`${this.config.driftApi}/collections/copy-documents`, {
      source_collection_id: sourceCollectionId,
      target_collection_id: targetCollectionId,
      document_ids: documentIds
    });
  }

  moveDocumentsToCollection(
    sourceCollectionId: string,
    targetCollectionId: string,
    documentIds: string[]
  ): Observable<void> {
    const requestBody = {
      source_collection_id: sourceCollectionId,
      target_collection_id: targetCollectionId,
      document_ids: documentIds
    };
    return this.http.post<void>(`${this.config.driftApi}/collections/move-documents`, requestBody);
  }
}
